import { Badge, Button } from "@design";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Text } from "@/components/Text";
import { BrandKitPreview } from "@/features/build/brands/BrandKitPreview";
import { DraftCard } from "../DraftCard";
import { useDraftContext } from "../DraftContext";
import type { EntityDraftCardProps } from "../registry";
import { useBrandDraft } from "./useBrandDraft";

const STYLE_PREVIEW = 400;

/** A brand draft: its palette as swatches, fonts and tone, and the writing style. */
export function BrandDraftCard({ draft, state, entityUuid, updatedAt, answerable }: Readonly<EntityDraftCardProps>) {
  const { t } = useTranslation("buildChat");
  const { linked } = useDraftContext();
  const brand = useBrandDraft(draft);
  const [all, setAll] = useState(false);
  const style = brand.body.brandStyle ?? "";
  const long = style.length > STYLE_PREVIEW;
  return (
    <DraftCard
      title={brand.body.name ?? linked.find((e) => e.uuid === draft.targetUuid)?.name ?? t("draft.untitled")}
      entityLabel={t("draft.entity.brand")}
      state={state}
      isUpdate={Boolean(draft.targetUuid)}
      doneUuid={brand.doneUuid}
      entityUuid={entityUuid}
      updatedAt={updatedAt}
      missingFields={draft.missingFields}
      questions={draft.questions}
      answerable={answerable}
      busy={brand.busy}
      steps={brand.steps}
      onCreate={brand.run}
      onRetry={brand.retry}
      onOpenInForm={brand.openInForm}
      detailPath={(uuid) => `/build/brands/${uuid}`}
      summary={
        <div className="space-y-3">
          {(brand.body.audience || brand.body.description) && (
            <div className="flex flex-wrap items-center gap-2">
              {brand.body.audience && <Badge variant="neutral">{brand.body.audience}</Badge>}
              {brand.body.description && <Text variant="body-sm">{brand.body.description}</Text>}
            </div>
          )}
          {brand.body.brandKit && <BrandKitPreview kit={brand.body.brandKit} withType />}
          {style && (
            <div className="space-y-1">
              <Text variant="caption" tone="tertiary">
                {t("brand.style")}
              </Text>
              <Text variant="body-sm" tone="secondary" className="whitespace-pre-wrap break-words">
                {all || !long ? style : `${style.slice(0, STYLE_PREVIEW)}…`}
              </Text>
              {long && (
                <Button variant="ghost" size="sm" onClick={() => setAll((open) => !open)}>
                  {all ? t("brand.showLess") : t("brand.showAll")}
                </Button>
              )}
            </div>
          )}
        </div>
      }
    />
  );
}
